/**
 * New Booking. CRM-BUILD, 2026-09-15.
 *
 * ┌─ ⛔ NOTHING IN THE PRODUCT COULD CREATE A BOOKING UNTIL TODAY ──────────────┐
 * │ `createBooking` had been built, tested and correct since wave 3, and no      │
 * │ screen called it. **The board showed bookings the seeder made**, and the     │
 * │ first thing anybody tries on a board is to add one. Amir 2026-09-15.        │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ THE DATE SET IS PART OF THE FORM, NOT A FOLLOW-UP. §4.17: a booking without
 * a date is *"a record you cannot act on"*, and the board card already says
 * `No date set` in those words. Creating one and asking somebody to fill the date
 * in afterwards would produce, on purpose, the record the product describes as
 * unusable.
 *
 * ⚠️ AND THE DATE FIELDS ARE WHATEVER THIS ACCOUNT CONFIGURED - `in_date_set`
 * rather than `field_type = 'date'`, which is the distinction an implementation
 * drops. A deployment with none gets a form without them and says so.
 */
import { useEffect, useRef, useState } from 'react'
import {
  createBooking,
  fetchNewBookingOptions,
  type NewBookingOptions,
} from '../data/new-booking-api.js'

export function NewBookingDialog({
  contactReference,
  onClose,
  onCreated,
}: {
  contactReference: number
  onClose: () => void
  onCreated: (reference: number) => void
}) {
  const [options, setOptions] = useState<NewBookingOptions | null>(null)
  const [pipelineId, setPipelineId] = useState('')
  const [stageId, setStageId] = useState('')
  const [title, setTitle] = useState('')
  const [value, setValue] = useState('')
  const [dates, setDates] = useState<Record<string, string>>({})
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const first = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    void fetchNewBookingOptions().then((o) => {
      setOptions(o)
      const p = o.pipelines[0]
      if (p !== undefined) {
        setPipelineId(p.id)
        setStageId(p.stages[0]?.id ?? '')
      }
    })
  }, [])

  useEffect(() => {
    first.current?.focus()
  }, [options !== null])

  const pipeline = options?.pipelines.find((p) => p.id === pipelineId)

  const submit = async () => {
    setBusy(true)
    setProblem(null)
    const result = await createBooking({
      contactReference,
      pipelineId,
      stageId,
      title,
      value,
      fields: Object.entries(dates)
        .filter(([, v]) => v !== '')
        .map(([fieldId, v]) => ({ fieldId, value: v })),
    })
    setBusy(false)
    if (result.ok) onCreated(result.reference)
    else setProblem(sentenceFor(result.reason, options))
  }

  /**
   * ⛔ NO PIPELINE IS ITS OWN SENTENCE, not a disabled button. A fresh
   * deployment has none, and `bookings.pipeline_id` is NOT NULL - so this is not
   * "you cannot do this yet", it is "the account has nothing to put it in", and
   * the thing to do next is different.
   */
  if (options !== null && options.pipelines.length === 0) {
    return (
      <div className="board__editor-scrim" role="dialog" aria-modal="true" data-testid="new-booking">
        <div className="board__editor">
          <h2 className="board__editor-title">New {options.entityLabel}</h2>
          <p className="board__editor-note" data-testid="new-booking-problem">
            No pipelines are configured yet, so there is nowhere to put a booking. Add one from Edit
            Stages on the Bookings board first.
          </p>
          <div className="board__editor-actions">
            <button type="button" onClick={onClose} data-testid="new-booking-cancel">
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="board__editor-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="New Booking"
      data-testid="new-booking"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <div className="board__editor">
        <h2 className="board__editor-title">New {options?.entityLabel ?? 'Booking'}</h2>

        <label className="board__editor-toggle" htmlFor="new-booking-title">
          Title
          <input
            id="new-booking-title"
            ref={first}
            data-testid="new-booking-title"
            value={title}
            maxLength={120}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        {/** ⚠️ A TITLE IS OPTIONAL AND THE FORM SAYS SO, because Amir's ruling A
         *  already decided what an untitled booking shows: the entity label and
         *  its reference. Making it required here would contradict that. */}
        <p className="board__editor-note">
          Optional. Without one it shows as {options?.entityLabel ?? 'Booking'} and its number.
        </p>

        <label className="board__editor-toggle" htmlFor="new-booking-pipeline">
          Pipeline
          <select
            id="new-booking-pipeline"
            data-testid="new-booking-pipeline"
            value={pipelineId}
            onChange={(e) => {
              setPipelineId(e.target.value)
              const p = options?.pipelines.find((x) => x.id === e.target.value)
              setStageId(p?.stages[0]?.id ?? '')
            }}
          >
            {(options?.pipelines ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>

        {/** ⛔ ACTIVE STAGES ONLY, AND THAT IS A DECISION rather than a filter.
         *  A booking opened directly at Won has no history: its `created` event
         *  would record a terminal opening stage and every duration analytics
         *  computes from that stream would be zero. Recording a past sale is an
         *  import, and item 23 owns it. */}
        <label className="board__editor-toggle" htmlFor="new-booking-stage">
          Stage
          <select
            id="new-booking-stage"
            data-testid="new-booking-stage"
            value={stageId}
            onChange={(e) => setStageId(e.target.value)}
          >
            {(pipeline?.stages ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <label className="board__editor-toggle" htmlFor="new-booking-value">
          {options?.valueLabel ?? 'Sales Value'}
          <input
            id="new-booking-value"
            data-testid="new-booking-value"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </label>
        {/** ⛔ EMPTY IS "not agreed yet" AND IS NOT ZERO, which is what the card
         *  renders and what 121 corpus bookings are. */}
        <p className="board__editor-note">Leave empty if it is not agreed yet.</p>

        {(options?.dateFields ?? []).map((f) => (
          <label key={f.id} className="board__editor-toggle" htmlFor={`new-booking-${f.id}`}>
            {f.label}
            <input
              id={`new-booking-${f.id}`}
              type="date"
              data-testid={`new-booking-date-${f.id}`}
              value={dates[f.id] ?? ''}
              onChange={(e) => setDates({ ...dates, [f.id]: e.target.value })}
            />
          </label>
        ))}
        {options !== null && options.dateFields.length === 0 && (
          <p className="board__editor-note" data-testid="new-booking-no-dates">
            This account has no booking date fields configured, so this booking will have no date. Add
            one under Custom Fields.
          </p>
        )}

        {problem !== null && (
          <p className="board__editor-error" role="alert" data-testid="new-booking-problem">
            {problem}
          </p>
        )}

        <div className="board__editor-actions">
          <button type="button" onClick={onClose} data-testid="new-booking-cancel">
            Cancel
          </button>
          <button
            type="button"
            className="board__editor-save"
            disabled={busy || stageId === ''}
            data-testid="new-booking-save"
            onClick={() => void submit()}
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** ⛔ The server's refusal in the product's words. The reason travels as a code
 *  so this can say the sentence, rather than the phrasing living on the wire. */
function sentenceFor(reason: string, options: NewBookingOptions | null): string {
  const entity = (options?.entityLabel ?? 'Booking').toLowerCase()
  switch (reason) {
    case 'no-pipeline':
      return 'No pipelines are configured yet, so there is nowhere to put it.'
    case 'stage-not-in-pipeline':
      return `That stage is not an open stage of this pipeline. A ${entity} starts in an open stage, because one opened at Won or Lost has no history to report on.`
    case 'contact-not-found':
      return 'That contact is no longer available. Close this and reopen it.'
    case 'bad-field':
      return `The ${entity} was created and one of its dates could not be saved. Open it and set the date there.`
    default:
      return `That ${entity} could not be created.`
  }
}
